import { useWatch } from '@kanda-libs/ks-component-ts';
import { useCurrentJob } from 'hooks';
import { FIELD_NAMES } from 'pages/Quote/constants';
import { useMemo } from 'react';
import { calculateLoanCosts } from 'utils';
import { CheckoutFinanceRate } from '../constants';

export interface FinanceInfo {
  jobTotal: number;
  monthly: number;
  deposit: number;
  term: string;
  interest: number;
  interestRate: number;
  total: number;
  duration: number;
  deferredDuration: number;
  isBuyNowPayLater: boolean;
}

export interface FinanceInfoHook {
  info: FinanceInfo;
}

export default function useFinanceInfo(
  selectedRate?: CheckoutFinanceRate,
): FinanceInfoHook {
  const { job, isLoading } = useCurrentJob();
  const [duration, loanAmount] = useWatch({
    name: [FIELD_NAMES.duration, FIELD_NAMES.editLoanAmount],
  });
  const loan = parseInt(loanAmount, 10);

  const info: FinanceInfo = useMemo(() => {
    if (!job || isLoading)
      return {
        jobTotal: 0,
        monthly: 0,
        deposit: 0,
        term: '10',
        interest: 0,
        interestRate: 0,
        total: 0,
        duration: 0,
        deferredDuration: 0,
        isBuyNowPayLater: false,
      };
    const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;

    const isBuyNowPayLater = selectedRate?.type === 'BUYNOW_PAYLATER';

    const deposit = loan ? jobTotal - loan : jobTotal;

    const values = calculateLoanCosts(
      {
        totalIncVat: jobTotal,
      },
      selectedRate?.name,
      deposit,
    );

    return {
      jobTotal,
      monthly: values.monthly,
      deposit,
      term: duration,
      interest: values.financeCost,
      total: values.total + deposit,
      interestRate: parseInt(selectedRate?.interest || '0', 10),
      duration: parseInt(selectedRate?.duration || '0', 10),
      deferredDuration: selectedRate?.deferredDuration || 0,
      isBuyNowPayLater,
    };
  }, [job, isLoading, duration, selectedRate, loan]);

  return {
    info,
  };
}
